import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useFarms, useLots, createAnimalLocal, findAnimalByLomoLocal } from "@/lib/repo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { ScanCTA } from "@/components/ScanCTA";
import { IronBrandCanvas } from "@/components/IronBrandCanvas";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { deriveCategory, CATEGORIES_BOVINO, CATEGORIES_BUFALINO } from "@/lib/utils";

export default function NewAnimal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledChip = searchParams.get("chip") || "";
  const prefilledFarm = searchParams.get("farm_id") || "";
  const farms = useFarms();
  const [data, setData] = useState({
    chip_code: prefilledChip, lomo_number: "", ear_tattoo: "",
    // Fix K (2026-09-18): raza por defecto "Mestizo" (pedido del cliente).
    sex: "female", breed: "Mestizo", birth_date: "",
    farm_id: prefilledFarm, lot_id: "", category: "", observations: "",
    iron_brand: "", iron_brand_drawing: null,
    mother_lomo: "", mother_id: null, mother_chip: null,
  });
  const lots = useLots(data.farm_id);
  const [saving, setSaving] = useState(false);
  const [motherHint, setMotherHint] = useState(null);
  const categoryTouched = useRef(false);

  useEffect(() => {
    if (farms[0] && !prefilledFarm && !data.farm_id) setData((d)=>({ ...d, farm_id: farms[0].id }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farms]);

  useEffect(() => {
    if (!categoryTouched.current && data.birth_date) {
      const c = deriveCategory(data.sex, data.birth_date);
      if (c) setData((d) => ({ ...d, category: c }));
    }
  }, [data.birth_date, data.sex]);

  const lookupMother = async (lomo) => {
    const m = await findAnimalByLomoLocal(lomo);
    if (m) {
      setData((d) => ({ ...d, mother_id: m.id, mother_chip: m.chip_code || null }));
      setMotherHint({ found: true, label: `Vinculada · Lomo ${m.lomo_number} · ${m.category || "vaca"}` });
    } else {
      setData((d) => ({ ...d, mother_id: null, mother_chip: null }));
      setMotherHint(lomo ? { found: false, label: "No está registrada — se guarda el número igual" } : null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.chip_code || data.chip_code.length < 10) return toast.error("Ingresá un chip válido (10+ dígitos)");
    if (!data.farm_id) return toast.error("Elegí una finca");
    setSaving(true);
    try {
      const payload = { ...data, lot_id: data.lot_id || null, category: data.category || null };
      const a = await createAnimalLocal(payload);
      toast.success("Animal cargado");
      navigate(`/animales/${a.id}`);
    } catch (err) {
      toast.error(err?.userMessage || "No pude guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate(-1)} className="h-9 px-2" data-testid="new-animal-back"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold">Nuevo animal</h1>
      </div>

      <form onSubmit={submit} className="space-y-4 bg-card border border-border rounded-2xl p-5">
        <div>
          <Label>Código de chip RFID</Label>
          <Input data-testid="new-animal-chip" required value={data.chip_code} onChange={(e)=>setData({ ...data, chip_code: e.target.value.replace(/\D/g, "") })} className="h-11 font-mono" placeholder="900164000000000" />
          <p className="text-xs text-muted-foreground mt-1">15 dígitos. Si escaneás, se completa solo.</p>
        </div>
        <ScanCTA testId="new-animal-scan-button" onChip={(c)=>setData((d)=>({ ...d, chip_code: c }))} />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Número de lomo</Label>
            <Input data-testid="new-animal-lomo" value={data.lomo_number} onChange={(e)=>setData({ ...data, lomo_number: e.target.value })} className="h-11" placeholder="A-1234" />
          </div>
          <div>
            <Label>Tatuaje de oreja</Label>
            <Input data-testid="new-animal-tattoo" value={data.ear_tattoo} onChange={(e)=>setData({ ...data, ear_tattoo: e.target.value })} className="h-11" placeholder="12-345" />
          </div>
          <div>
            <Label>Sexo</Label>
            <Select value={data.sex} onValueChange={(v)=>setData({ ...data, sex: v })}>
              <SelectTrigger className="h-11" data-testid="new-animal-sex"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="female">Hembra</SelectItem>
                <SelectItem value="male">Macho</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Raza</Label>
            <Input data-testid="new-animal-breed" value={data.breed} onChange={(e)=>setData({ ...data, breed: e.target.value })} className="h-11" />
          </div>
          <div>
            <Label>Fecha de nacimiento</Label>
            <Input data-testid="new-animal-birth" type="date" value={data.birth_date} onChange={(e)=>setData({ ...data, birth_date: e.target.value })} className="h-11" />
          </div>
          <div>
            <Label>Categoría</Label>
            <Select value={data.category || "auto"} onValueChange={(v)=>{ categoryTouched.current = v !== "auto"; setData({ ...data, category: v === "auto" ? "" : v }); }}>
              <SelectTrigger className="h-11" data-testid="new-animal-category"><SelectValue placeholder="Auto" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (por sexo + edad)</SelectItem>
                <SelectGroup>
                  <SelectLabel>Bovino</SelectLabel>
                  {CATEGORIES_BOVINO.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Bufalino</SelectLabel>
                  {CATEGORIES_BUFALINO.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectGroup>
              </SelectContent>
            </Select>
            {/* Fix E (2026-08-24): rangos definidos por el cliente, visibles al cargar. */}
            <p className="text-xs text-muted-foreground mt-1" data-testid="category-ranges-hint">
              Becerro/a: 0-8 meses · Mauta: 9-17 · Novilla: 18-24 · Maute: 9-24 · Vaca/Toro: 25+
            </p>
          </div>
          <div>
            <Label>Lomo de la madre</Label>
            <Input data-testid="new-animal-mother-lomo" value={data.mother_lomo} onChange={(e)=>setData({ ...data, mother_lomo: e.target.value })} onBlur={()=>lookupMother(data.mother_lomo)} className="h-11" placeholder="Opcional" />
            {motherHint && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${motherHint.found ? "text-[hsl(var(--state-present))]" : "text-muted-foreground"}`} data-testid="new-animal-mother-hint">
                {motherHint.found && <CheckCircle2 size={12} />}{motherHint.label}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Finca</Label>
            <Select value={data.farm_id} onValueChange={(v)=>setData({ ...data, farm_id: v, lot_id: "" })}>
              <SelectTrigger className="h-11" data-testid="new-animal-farm"><SelectValue placeholder="Elegir" /></SelectTrigger>
              <SelectContent>
                {farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Lote (opcional)</Label>
            <Select value={data.lot_id || "none"} onValueChange={(v)=>setData({ ...data, lot_id: v === "none" ? "" : v })}>
              <SelectTrigger className="h-11" data-testid="new-animal-lot"><SelectValue placeholder="Sin lote" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin lote</SelectItem>
                {lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Observaciones</Label>
          <Textarea data-testid="new-animal-notes" value={data.observations} onChange={(e)=>setData({ ...data, observations: e.target.value })} rows={3} placeholder="Anotá algo útil para el encargado o el veterinario…" />
        </div>

        <div>
          <Label>Hierro (pierna / paleta)</Label>
          <Input data-testid="new-animal-iron-brand" value={data.iron_brand} onChange={(e)=>setData({ ...data, iron_brand: e.target.value })} className="h-11" placeholder="Marca de hierro" />
          <p className="text-xs text-muted-foreground mt-1 mb-2">Podés además dibujar el diseño del hierro para identificarlo mejor.</p>
          <IronBrandCanvas value={data.iron_brand_drawing} onChange={(v)=>setData({ ...data, iron_brand_drawing: v })} />
        </div>

        <div className="flex gap-2">
          <Button data-testid="new-animal-submit" type="submit" disabled={saving} className="h-12 bg-emerald-700 hover:bg-emerald-800 flex-1">
            {saving ? "Guardando…" : "Guardar animal"}
          </Button>
          <Button type="button" variant="outline" className="h-12" onClick={()=>navigate(-1)}>Cancelar</Button>
        </div>
      </form>
    </div>
  );
}
